import React, { useState } from 'react';
import collectionsData from '../../collections/collections.json';

export default function ApiCollections({ onRequestSelect }) {
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);

  const toggleFolder = (folderName) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  const handleRequestClick = (request, folderName, requestIndex) => {
    const requestId = `${folderName}-${requestIndex}`;
    setSelectedRequest(requestId);
    onRequestSelect(request);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'text-green-600 bg-green-50 border-green-200',
      POST: 'text-blue-600 bg-blue-50 border-blue-200',
      PUT: 'text-orange-600 bg-orange-50 border-orange-200',
      PATCH: 'text-purple-600 bg-purple-50 border-purple-200',
      DELETE: 'text-red-600 bg-red-50 border-red-200',
    };
    return colors[method] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className="w-full bg-white h-screen overflow-hidden border-r border-gray-200 flex flex-col shadow-lg">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-5 h-5 text-[#fdb814]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h2 className="text-base font-bold text-gray-800">API Collections</h2>
        </div>
        <p className="text-xs text-gray-500">Pre-configured API requests</p>
      </div>

      {/* Collections List */}
      <div className="flex-1 overflow-y-auto pb-4">
        {collectionsData.collections.map((collection, collectionIndex) => (
          <div key={collectionIndex} className="border-b border-gray-100 last:border-b-0">
            {/* Collection Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-[#fdb814]/5 to-transparent">
              <h3 className="text-xs font-bold text-[#fdb814] uppercase tracking-wide flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
                {collection.name}
              </h3>
            </div>

            {/* Folders */}
            {collection.folders.map((folder, folderIndex) => (
              <div key={folderIndex}>
                <button
                  className="w-full text-left px-4 py-2.5 flex justify-between items-center hover:bg-gray-50 transition-all duration-200 group"
                  onClick={() => toggleFolder(folder.name)}
                >
                  <div className="flex items-center gap-2">
                    <svg 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedFolders[folder.name] ? 'rotate-90' : ''}`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#fdb814] transition-colors">
                      {folder.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {folder.requests.length}
                  </span>
                </button>

                {/* Requests */}
                {expandedFolders[folder.name] && (
                  <div className="bg-gray-50/50">
                    {folder.requests.map((request, reqIndex) => {
                      const requestId = `${folder.name}-${reqIndex}`;
                      const isSelected = selectedRequest === requestId;
                      return (
                        <button
                          key={reqIndex}
                          className={`w-full text-left px-4 py-2.5 pl-10 hover:bg-white transition-all duration-200 border-l-2 ${
                            isSelected 
                              ? 'bg-white border-[#fdb814] shadow-sm' 
                              : 'border-transparent hover:border-gray-300'
                          }`}
                          onClick={() => handleRequestClick(request, folder.name, reqIndex)}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs font-bold rounded border ${getMethodColor(request.method)}`}>
                              {request.method}
                            </span>
                            <span className={`text-xs font-medium truncate ${
                              isSelected ? 'text-[#fdb814]' : 'text-gray-700'
                            }`}>
                              {request.name}
                            </span>
                          </div>
                          {request.url && (
                            <p className="text-xs text-gray-400 truncate pl-0.5" title={request.url}>
                              {request.url}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>Click any request to load</span>
        </div>
      </div>
    </div>
  );
}